// PlayerRoleManagement.jsx
// Comprehensive role management for club admins to assign/remove admin and treasurer roles

import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, addDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Shield, UserPlus, UserMinus, Check, X, AlertCircle, Search, Users, Crown, ArrowLeft, Star, User, ChevronDown } from 'lucide-react';
import { formatDate } from '../../utils/helpers';
import MigratePlayersModal from './MigratePlayersModal';

const PlayerRoleManagement = ({ currentUser, selectedClub, onBack }) => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingRole, setUpdatingRole] = useState(null);
  const [checkingPermissions, setCheckingPermissions] = useState(true);
  const [expandedPlayer, setExpandedPlayer] = useState(null);
  const [clubAdmins, setClubAdmins] = useState([]);
  
  // Migration feature
  const [selectedPlayerIds, setSelectedPlayerIds] = useState(new Set());
  const [showMigrateModal, setShowMigrateModal] = useState(false);

  // Check if current user can manage roles (must be a club admin)
  useEffect(() => {
    const checkPermissions = async () => {
      if (!currentUser?.uid || !selectedClub?.id) {
        setCheckingPermissions(false);
        return;
      }

      try {
        // Get the club document to check admins array
        const clubRef = doc(db, 'club', selectedClub.id);
        const clubDoc = await getDoc(clubRef);

        if (clubDoc.exists()) {
          const admins = clubDoc.data().admins || [];
          setClubAdmins(admins);
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, [currentUser, selectedClub]);

  const canManageRoles = () => {
    if (!currentUser || !selectedClub) return false;
    
    const inClubAdmins = clubAdmins.includes(currentUser.uid) || clubAdmins.includes(currentUser.email);
    const inPermissions = currentUser.permissions?.clubAdminOf?.includes(selectedClub.id);
    const inJoinedClubs = currentUser.joinedClubs?.[selectedClub.id]?.role === 'admin';
    const isSuperAdmin = currentUser.clubInfo?.role === 'super-admin' || currentUser.role === 'super-admin';
    
    return inClubAdmins || inPermissions || inJoinedClubs || isSuperAdmin;
  };

  useEffect(() => {
    if (selectedClub?.id && !checkingPermissions) {
      loadClubMembers();
    }
  }, [selectedClub, checkingPermissions]);

  const loadClubMembers = async () => {
    setLoading(true);
    setMessage('');
    try {
      // Get club document for admins array
      const clubRef = doc(db, 'club', selectedClub.id);
      const clubDoc = await getDoc(clubRef);
      let currentAdmins = [];

      if (clubDoc.exists()) {
        currentAdmins = clubDoc.data().admins || [];
      }

      // Get all club members
      const usersRef = collection(db, 'users');
      const qLegacy = query(usersRef, where('clubInfo.clubId', '==', selectedClub.id));
      const qNew = query(usersRef, where('joinedClubsIds', 'array-contains', selectedClub.id));

      const [snap1, snap2] = await Promise.all([getDocs(qLegacy), getDocs(qNew)]);

      const members = [];
      const seenIds = new Set();

      const processDoc = (doc) => {
        if (seenIds.has(doc.id)) return;
        seenIds.add(doc.id);

        const data = doc.data();
        const clubInfo = data.joinedClubs?.[selectedClub.id] || (data.clubInfo?.clubId === selectedClub.id ? data.clubInfo : null) || (data.clubId === selectedClub.id ? data : null);

        // Consider approved if explicitly 'approved', or if they are legacy (missing status but match clubId), or if they are currently an admin
        const isLegacyMatch = (data.clubId === selectedClub.id || data.clubInfo?.clubId === selectedClub.id) && clubInfo?.status !== 'rejected';
        const isApprovedStatus = clubInfo?.status === 'approved';
        const isAlreadyAdmin = currentAdmins.includes(doc.id) || currentAdmins.includes(data.email);

        if (isApprovedStatus || isLegacyMatch || isAlreadyAdmin) {
          const userId = doc.id;
          const userEmail = data.email;

          // Determine all roles for this user
          const roles = [];

          // Check if admin (in array or by role)
          if (currentAdmins.includes(userId) || currentAdmins.includes(userEmail)) {
            roles.push('admin');
          }

          // Check if treasurer
          if (clubInfo?.role === 'treasurer' ||
            clubInfo?.additionalRoles?.includes('treasurer')) {
            roles.push('treasurer');
          }

          // Check if auditor
          if (clubInfo?.role === 'auditor' ||
            clubInfo?.additionalRoles?.includes('auditor')) {
            roles.push('auditor');
          }

          // If no special roles, they're a player
          if (roles.length === 0) {
            roles.push('player');
          }

          members.push({
            id: userId,
            name: data.name || data.displayName || 'Unknown',
            email: userEmail,
            photoURL: data.photoURL,
            phoneNumber: data.phoneNumber,
            roles: roles,
            isAdmin: roles.includes('admin'),
            isTreasurer: roles.includes('treasurer'),
            isAuditor: roles.includes('auditor'),
            isPlayer: roles.length === 0 || (roles.length === 1 && roles[0] === 'player'),
            joinedAt: clubInfo?.joinedAt,
            additionalRoles: clubInfo?.additionalRoles || []
          });
        }
      };

      snap1.forEach(processDoc);
      snap2.forEach(processDoc);

      setPlayers(members.sort((a, b) => {
        // Sort: Admins first, then treasurers, then players
        if (a.isAdmin && !b.isAdmin) return -1;
        if (!a.isAdmin && b.isAdmin) return 1;
        if (a.isTreasurer && !b.isTreasurer) return -1;
        if (!a.isTreasurer && b.isTreasurer) return 1;
        if (a.isAuditor && !b.isAuditor) return -1;
        if (!a.isAuditor && b.isAuditor) return 1;
        return a.name.localeCompare(b.name);
      }));
    } catch (error) {
      console.error('Error loading club members:', error);
      setMessage({ type: 'error', text: 'Failed to load club members' });
    } finally {
      setLoading(false);
    }
  };

  const assignAdminRole = async (playerId, playerName) => {
    if (!canManageRoles()) {
      setMessage({ type: 'error', text: 'You do not have permission to manage roles' });
      return;
    }

    const confirmMsg = `Are you sure you want to make ${playerName} a club admin?\n\nThey will be able to:\n• Manage teams and players\n• Create games and tournaments\n• View all financial data\n• Assign roles to other members`;

    if (!confirm(confirmMsg)) return;

    setUpdatingRole(playerId);
    try {
      // Add to club's admins array
      const clubRef = doc(db, 'club', selectedClub.id);
      const playerDoc = await getDoc(doc(db, 'users', playerId));

      if (playerDoc.exists()) {
        const playerEmail = playerDoc.data().email;
        const currentData = playerDoc.data();

        // 1. Update Club: Only add ID (as requested)
        await updateDoc(clubRef, {
          admins: arrayUnion(playerId)
        });

        // 2. Update User: Update primary role to 'admin' AND set per-club role
        const primaryClubId = currentData?.clubId || currentData?.clubInfo?.clubId;
        const updatePayload = {
          [`joinedClubs.${selectedClub.id}.role`]: 'admin',
          joinedClubsIds: arrayUnion(selectedClub.id),
          promotedAt: new Date().toISOString(),
          promotedBy: currentUser.uid
        };

        if (primaryClubId === selectedClub.id || !primaryClubId) {
          updatePayload.role = 'admin';
        }

        await updateDoc(doc(db, 'users', playerId), updatePayload);

        // Create audit log
        await addDoc(collection(db, 'auditLogs'), {
          action: 'ADMIN_ROLE_ASSIGNED',
          targetUserId: playerId,
          targetUserName: playerName,
          performedBy: currentUser.uid,
          performedByName: currentUser.displayName || currentUser.email,
          timestamp: new Date().toISOString(),
          clubId: selectedClub.id,
          clubName: selectedClub.name
        });

        setMessage({
          type: 'success',
          text: `✅ ${playerName} is now a club admin!`
        });

        await loadClubMembers();
      }
    } catch (error) {
      console.error('Error assigning admin role:', error);
      setMessage({
        type: 'error',
        text: `Failed to assign admin role to ${playerName}`
      });
    } finally {
      setUpdatingRole(null);
    }
  };

  const removeAdminRole = async (playerId, playerName) => {
    if (!canManageRoles()) {
      setMessage({ type: 'error', text: 'You do not have permission to manage roles' });
      return;
    }

    // Check if this is the last admin
    const adminCount = players.filter(p => p.isAdmin).length;
    if (adminCount <= 1) {
      alert(`⚠️ Cannot remove admin role from ${playerName} as they are the last admin. Clubs must have at least one admin.`);
      return;
    }

    // Prevent self-demotion
    if (playerId === currentUser.uid) {
      alert('You cannot remove your own admin role. Another admin must do this.');
      return;
    }

    const confirmMsg = `Remove admin role from ${playerName}?\n\nThey will no longer be able to manage the club.`;
    if (!confirm(confirmMsg)) return;

    setUpdatingRole(playerId);
    try {
      // Remove from club's admins array
      const clubRef = doc(db, 'club', selectedClub.id);
      const playerDocRef = doc(db, 'users', playerId);
      const playerDoc = await getDoc(playerDocRef);

      if (playerDoc.exists()) {
        const playerEmail = playerDoc.data().email;
        const playerData = playerDoc.data();

        // 1. Update Club: Remove ID (and email for cleanup)
        await updateDoc(clubRef, {
          admins: arrayRemove(playerId, playerEmail)
        });

        // 2. Update User: Set per-club role to 'player'
        const userUpdatePayload = {
          [`joinedClubs.${selectedClub.id}.role`]: 'player',
          additionalRoles: arrayRemove('admin'), // Clean up if it exists
          demotedAt: new Date().toISOString(),
          demotedBy: currentUser.uid
        };

        // Check if user is admin of any OTHER club before resetting global role
        let isAdminOfOtherClub = false;
        const joinedClubs = playerData.joinedClubs || {};
        for (const [jClubId, jClubData] of Object.entries(joinedClubs)) {
          if (jClubId !== selectedClub.id && jClubData.role === 'admin') {
            isAdminOfOtherClub = true;
            break;
          }
        }
        // Also check other club.admins arrays if joinedClubs is incomplete
        if (!isAdminOfOtherClub) {
          const clubsCollectionRef = collection(db, 'club');
          const allClubsSnap = await getDocs(clubsCollectionRef);
          allClubsSnap.forEach((clubDoc) => {
            if (clubDoc.id !== selectedClub.id) {
              const clubData = clubDoc.data();
              if (clubData.admins?.includes(playerId)) {
                isAdminOfOtherClub = true;
              }
            }
          });
        }

        if (!isAdminOfOtherClub) {
          userUpdatePayload.role = 'player';
          console.log('✅ User is not admin of any other club, resetting global role to player');
        } else {
          console.log('ℹ️ User is still admin of another club, keeping global role as admin');
        }

        await updateDoc(playerDocRef, userUpdatePayload);

        // Create audit log
        await addDoc(collection(db, 'auditLogs'), {
          action: 'ADMIN_ROLE_REMOVED',
          targetUserId: playerId,
          targetUserName: playerName,
          performedBy: currentUser.uid,
          performedByName: currentUser.displayName || currentUser.email,
          timestamp: new Date().toISOString(),
          clubId: selectedClub.id,
          clubName: selectedClub.name
        });

        setMessage({
          type: 'success',
          text: `✅ Admin role removed from ${playerName}`
        });

        await loadClubMembers();
      }
    } catch (error) {
      console.error('Error removing admin role:', error);
      setMessage({
        type: 'error',
        text: `Failed to remove admin role from ${playerName}`
      });
    } finally {
      setUpdatingRole(null);
    }
  };

  const assignTreasurerRole = async (playerId, playerName) => {
    if (!canManageRoles()) {
      setMessage({ type: 'error', text: 'You do not have permission to manage roles' });
      return;
    }

    const confirmMsg = `Are you sure you want to make ${playerName} a treasurer?\n\nThey will be able to:\n• Approve and deny expenses\n• Approve payment submissions\n• View all financial reports`;

    if (!confirm(confirmMsg)) return;

    setUpdatingRole(playerId);
    try {
      const playerRef = doc(db, 'users', playerId);
      const playerDoc = await getDoc(playerRef);

      if (playerDoc.exists()) {
        const currentData = playerDoc.data();
        const currentAdditionalRoles = currentData.clubInfo?.additionalRoles || [];

        if (!currentAdditionalRoles.includes('treasurer')) {
          currentAdditionalRoles.push('treasurer');
        }

        // Update club document map
        const clubRef = doc(db, 'club', selectedClub.id);
        await updateDoc(clubRef, {
          treasurers: arrayUnion(playerId)
        });

        // Update root-level additionalRoles for consistent access
        await updateDoc(playerRef, {
          additionalRoles: currentAdditionalRoles,
          'clubInfo.additionalRoles': currentAdditionalRoles, // Keep legacy path for safety
          'clubInfo.treasurerAssignedAt': new Date().toISOString(),
          'clubInfo.treasurerAssignedBy': currentUser.uid
        });

        // Create audit log
        await addDoc(collection(db, 'auditLogs'), {
          action: 'TREASURER_ROLE_ASSIGNED',
          targetUserId: playerId,
          targetUserName: playerName,
          performedBy: currentUser.uid,
          performedByName: currentUser.displayName || currentUser.email,
          timestamp: new Date().toISOString(),
          clubId: selectedClub.id,
          clubName: selectedClub.name
        });

        setMessage({
          type: 'success',
          text: `✅ ${playerName} is now a treasurer!`
        });

        await loadClubMembers();
      }
    } catch (error) {
      console.error('Error assigning treasurer role:', error);
      setMessage({
        type: 'error',
        text: `Failed to assign treasurer role to ${playerName}`
      });
    } finally {
      setUpdatingRole(null);
    }
  };

  const removeTreasurerRole = async (playerId, playerName) => {
    if (!canManageRoles()) {
      setMessage({ type: 'error', text: 'You do not have permission to manage roles' });
      return;
    }

    const confirmMsg = `Remove treasurer role from ${playerName}?\n\nThey will no longer be able to approve expenses or payments.`;
    if (!confirm(confirmMsg)) return;

    setUpdatingRole(playerId);
    try {
      const playerRef = doc(db, 'users', playerId);
      const playerDoc = await getDoc(playerRef);

      if (playerDoc.exists()) {
        const currentData = playerDoc.data();
        const currentAdditionalRoles = currentData.additionalRoles || currentData.clubInfo?.additionalRoles || [];
        const updatedRoles = currentAdditionalRoles.filter(role => role !== 'treasurer');

        // Update club document map
        const clubRef = doc(db, 'club', selectedClub.id);
        await updateDoc(clubRef, {
          treasurers: arrayRemove(playerId)
        });

        await updateDoc(playerRef, {
          additionalRoles: updatedRoles, // Update root
          'clubInfo.additionalRoles': updatedRoles, // Update legacy
          'clubInfo.treasurerRemovedAt': new Date().toISOString(),
          'clubInfo.treasurerRemovedBy': currentUser.uid
        });

        // Create audit log
        await addDoc(collection(db, 'auditLogs'), {
          action: 'TREASURER_ROLE_REMOVED',
          targetUserId: playerId,
          targetUserName: playerName,
          performedBy: currentUser.uid,
          performedByName: currentUser.displayName || currentUser.email,
          timestamp: new Date().toISOString(),
          clubId: selectedClub.id,
          clubName: selectedClub.name
        });

        setMessage({
          type: 'success',
          text: `✅ Treasurer role removed from ${playerName}`
        });

        await loadClubMembers();
      }
    } catch (error) {
      console.error('Error removing treasurer role:', error);
      setMessage({
        type: 'error',
        text: `Failed to remove treasurer role from ${playerName}`
      });
    } finally {
      setUpdatingRole(null);
    }
  };

  const assignAuditorRole = async (playerId, playerName) => {
    if (!canManageRoles()) {
      setMessage({ type: 'error', text: 'You do not have permission to manage roles' });
      return;
    }

    const confirmMsg = `Are you sure you want to make ${playerName} an auditor?\n\nThey will be able to:\n• View transaction audit reports`;

    if (!confirm(confirmMsg)) return;

    setUpdatingRole(playerId);
    try {
      const playerRef = doc(db, 'users', playerId);
      const playerDoc = await getDoc(playerRef);

      if (playerDoc.exists()) {
        const currentData = playerDoc.data();
        const currentAdditionalRoles = currentData.clubInfo?.additionalRoles || [];

        if (!currentAdditionalRoles.includes('auditor')) {
          currentAdditionalRoles.push('auditor');
        }

        // Update club document map
        const clubRef = doc(db, 'club', selectedClub.id);
        await updateDoc(clubRef, {
          auditors: arrayUnion(playerId)
        });

        // Update root-level additionalRoles for consistent access
        await updateDoc(playerRef, {
          additionalRoles: currentAdditionalRoles,
          'clubInfo.additionalRoles': currentAdditionalRoles,
          'clubInfo.auditorAssignedAt': new Date().toISOString(),
          'clubInfo.auditorAssignedBy': currentUser.uid
        });

        // Create audit log
        await addDoc(collection(db, 'auditLogs'), {
          action: 'AUDITOR_ROLE_ASSIGNED',
          targetUserId: playerId,
          targetUserName: playerName,
          performedBy: currentUser.uid,
          performedByName: currentUser.displayName || currentUser.email,
          timestamp: new Date().toISOString(),
          clubId: selectedClub.id,
          clubName: selectedClub.name
        });

        setMessage({
          type: 'success',
          text: `✅ ${playerName} is now an auditor!`
        });

        await loadClubMembers();
      }
    } catch (error) {
      console.error('Error assigning auditor role:', error);
      setMessage({
        type: 'error',
        text: `Failed to assign auditor role to ${playerName}`
      });
    } finally {
      setUpdatingRole(null);
    }
  };

  const removeAuditorRole = async (playerId, playerName) => {
    if (!canManageRoles()) {
      setMessage({ type: 'error', text: 'You do not have permission to manage roles' });
      return;
    }

    const confirmMsg = `Remove auditor role from ${playerName}?`;
    if (!confirm(confirmMsg)) return;

    setUpdatingRole(playerId);
    try {
      const playerRef = doc(db, 'users', playerId);
      const playerDoc = await getDoc(playerRef);

      if (playerDoc.exists()) {
        const currentData = playerDoc.data();
        const currentAdditionalRoles = currentData.additionalRoles || currentData.clubInfo?.additionalRoles || [];
        const updatedRoles = currentAdditionalRoles.filter(role => role !== 'auditor');

        // Update club document map
        const clubRef = doc(db, 'club', selectedClub.id);
        await updateDoc(clubRef, {
          auditors: arrayRemove(playerId)
        });

        await updateDoc(playerRef, {
          additionalRoles: updatedRoles, // Update root
          'clubInfo.additionalRoles': updatedRoles, // Update legacy
          'clubInfo.auditorRemovedAt': new Date().toISOString(),
          'clubInfo.auditorRemovedBy': currentUser.uid
        });

        // Create audit log
        await addDoc(collection(db, 'auditLogs'), {
          action: 'AUDITOR_ROLE_REMOVED',
          targetUserId: playerId,
          targetUserName: playerName,
          performedBy: currentUser.uid,
          performedByName: currentUser.displayName || currentUser.email,
          timestamp: new Date().toISOString(),
          clubId: selectedClub.id,
          clubName: selectedClub.name
        });

        setMessage({
          type: 'success',
          text: `✅ Auditor role removed from ${playerName}`
        });

        await loadClubMembers();
      }
    } catch (error) {
      console.error('Error removing auditor role:', error);
      setMessage({
        type: 'error',
        text: `Failed to remove auditor role from ${playerName}`
      });
    } finally {
      setUpdatingRole(null);
    }
  };

  // Filter players based on search
  const filteredPlayers = players.filter(player =>
    player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    player.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Count statistics
  const adminCount = players.filter(p => p.isAdmin).length;
  const treasurerCount = players.filter(p => p.isTreasurer).length;
  const auditorCount = players.filter(p => p.isAuditor).length;
  const playerCount = players.filter(p => p.isPlayer).length;

  // Loading state
  if (checkingPermissions) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Checking permissions...</p>
          </div>
        </div>
      </div>
    );
  }

  // Permission check
  if (!canManageRoles()) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 rounded-lg p-8 text-center">
            <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-red-900 mb-2">Access Denied</h3>
            <p className="text-red-700 mb-4">
              Only club admins can manage player roles.
            </p>
            {onBack && (
              <button
                onClick={onBack}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2 mx-auto"
              >
                <ArrowLeft size={16} />
                Return to Dashboard
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const togglePlayerSelection = (playerId) => {
    const newSelection = new Set(selectedPlayerIds);
    if (newSelection.has(playerId)) {
      newSelection.delete(playerId);
    } else {
      newSelection.add(playerId);
    }
    setSelectedPlayerIds(newSelection);
  };
  
  const toggleAllSelection = () => {
    if (selectedPlayerIds.size === filteredPlayers.length) {
      setSelectedPlayerIds(new Set());
    } else {
      setSelectedPlayerIds(new Set(filteredPlayers.map(p => p.id)));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Player Role Management</h1>
                <p className="text-gray-600">
                  Manage admin and treasurer roles for club members
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Message Display */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
            <div className="flex items-center gap-2">
              {message.type === 'success' ? (
                <Check className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <p className="font-medium">{message.text}</p>
            </div>
          </div>
        )}

        {/* Role Descriptions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Club Admin
              </h3>
              <ul className="space-y-1 text-sm text-blue-800">
                <li>• Manage teams and players</li>
                <li>• Create games and tournaments</li>
                <li>• View financial reports</li>
                <li>• Assign roles to members</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-600" />
                Treasurer
              </h3>
              <ul className="space-y-1 text-sm text-yellow-800">
                <li>• Approve/deny expenses</li>
                <li>• Approve payment submissions</li>
                <li>• View all financial reports</li>
                <li>• Cannot approve own expenses</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Auditor
              </h3>
              <ul className="space-y-1 text-sm text-purple-800">
                <li>• View Transaction Audit Report</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <User className="w-5 h-5" />
                Player
              </h3>
              <ul className="space-y-1 text-sm text-gray-700">
                <li>• Submit expenses</li>
                <li>• Make payments</li>
                <li>• View own balance</li>
                <li>• RSVP to games</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Club Admins</p>
                <p className="text-3xl font-bold text-blue-600">{adminCount}</p>
              </div>
              <Shield className="w-10 h-10 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border-2 border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Treasurers</p>
                <p className="text-3xl font-bold text-yellow-600">{treasurerCount}</p>
              </div>
              <Crown className="w-10 h-10 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border-2 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Auditors</p>
                <p className="text-3xl font-bold text-purple-600">{auditorCount}</p>
              </div>
              <Shield className="w-10 h-10 text-purple-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Players</p>
                <p className="text-3xl font-bold text-gray-700">{playerCount}</p>
              </div>
              <Users className="w-10 h-10 text-gray-500" />
            </div>
          </div>
        </div>

        {/* Search and Bulk Actions */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 flex flex-col sm:flex-row gap-4 items-center justify-between mb-6">
          <div className="relative w-full sm:w-96 flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
          </div>
          
          <div className="flex gap-2 w-full sm:w-auto items-center justify-between sm:justify-end">
             {selectedPlayerIds.size > 0 && (
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-600">
                    {selectedPlayerIds.size} selected
                  </span>
                  <button
                    onClick={() => setShowMigrateModal(true)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm shadow-sm transition-all"
                  >
                    Migrate Players
                  </button>
                </div>
             )}
          </div>
        </div>

        {/* Players List */}
        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading club members...</p>
              </div>
            ) : filteredPlayers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-600">
                  {searchTerm ? 'No members found matching your search' : 'No members found'}
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {filteredPlayers.length > 0 && (
                   <div className="bg-gray-50 flex items-center gap-3 px-6 py-3 border-b border-gray-200 rounded-t-lg">
                      <input 
                        type="checkbox" 
                        className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                        checked={filteredPlayers.length > 0 && selectedPlayerIds.size === filteredPlayers.length}
                        onChange={toggleAllSelection}
                      />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Select All Filtered Players</span>
                   </div>
                )}
                
                {filteredPlayers.map((player) => (
                  <div
                    key={player.id}
                    className={`border-l-4 transition-all duration-200 border-2 rounded-lg ${
                      player.isAdmin ? 'border-l-blue-500 bg-blue-50/10 hover:border-gray-300' :
                      player.isTreasurer ? 'border-l-yellow-500 bg-yellow-50/10 hover:border-gray-300' :
                      player.isAuditor ? 'border-l-purple-500 bg-purple-50/10 hover:border-gray-300' :
                      'border-l-transparent border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div
                      className="p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 cursor-pointer"
                      onClick={(e) => {
                        // Don't expand if clicking checkbox
                        if (e.target.type !== 'checkbox') {
                          setExpandedPlayer(expandedPlayer === player.id ? null : player.id);
                        }
                      }}
                    >
                      <div className="flex items-center gap-4 flex-1 w-full overflow-hidden">
                        <div className="flex items-center h-full pt-1.5 px-2">
                           <input 
                             type="checkbox" 
                             className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                             checked={selectedPlayerIds.has(player.id)}
                             onChange={() => togglePlayerSelection(player.id)}
                             onClick={(e) => e.stopPropagation()} // Prevent expansion when checking
                           />
                        </div>
                        {/* Avatar */}
                        {player.photoURL ? (
                          <img
                            src={player.photoURL}
                            alt={player.name}
                            className="w-12 h-12 rounded-full object-cover shadow-sm border border-gray-200"
                          />
                        ) : (
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${player.isAdmin ? 'bg-blue-500' :
                            player.isTreasurer ? 'bg-yellow-500' :
                              player.isAuditor ? 'bg-purple-500' :
                                'bg-gray-400'
                            }`}>
                            {player.name.charAt(0).toUpperCase()}
                          </div>
                        )}

                        {/* Player Info */}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900">{player.name}</p>
                            {/* Role Badges */}
                            {player.isAdmin && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-semibold">
                                Admin
                              </span>
                            )}
                            {player.isTreasurer && (
                              <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded-full font-semibold">
                                Treasurer
                              </span>
                            )}
                            {player.isAuditor && (
                              <span className="px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full font-semibold">
                                Auditor
                              </span>
                            )}
                            {player.id === currentUser.uid && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                                You
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{player.email}</p>
                        </div>
                      </div>

                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${expandedPlayer === player.id ? 'transform rotate-180' : ''
                        }`} />
                    </div>

                    {/* Expanded Actions */}
                    {expandedPlayer === player.id && (
                      <div className="border-t border-gray-200 p-4 bg-gray-50">
                        <div className="flex flex-wrap gap-2">
                          {player.id === currentUser.uid ? (
                            <span className="text-sm text-gray-500 italic">
                              You cannot modify your own roles
                            </span>
                          ) : updatingRole === player.id ? (
                            <div className="flex items-center gap-2">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                              <span className="text-sm text-gray-600">Updating...</span>
                            </div>
                          ) : (
                            <>
                              {/* Admin Role Toggle */}
                              {player.isAdmin ? (
                                <button
                                  onClick={() => removeAdminRole(player.id, player.name)}
                                  className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                                  disabled={updatingRole !== null}
                                >
                                  <UserMinus className="w-4 h-4" />
                                  Remove Admin
                                </button>
                              ) : (
                                <button
                                  onClick={() => assignAdminRole(player.id, player.name)}
                                  className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                                  disabled={updatingRole !== null}
                                >
                                  <UserPlus className="w-4 h-4" />
                                  Make Admin
                                </button>
                              )}

                              {/* Treasurer Role Toggle */}
                              {player.isTreasurer ? (
                                <button
                                  onClick={() => removeTreasurerRole(player.id, player.name)}
                                  className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                                  disabled={updatingRole !== null}
                                >
                                  <UserMinus className="w-4 h-4" />
                                  Remove Treasurer
                                </button>
                              ) : (
                                <button
                                  onClick={() => assignTreasurerRole(player.id, player.name)}
                                  className="px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                                  disabled={updatingRole !== null}
                                >
                                  <UserPlus className="w-4 h-4" />
                                  Make Treasurer
                                </button>
                              )}

                              {/* Auditor Role Toggle */}
                              {player.isAuditor ? (
                                <button
                                  onClick={() => removeAuditorRole(player.id, player.name)}
                                  className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                                  disabled={updatingRole !== null}
                                >
                                  <UserMinus className="w-4 h-4" />
                                  Remove Auditor
                                </button>
                              ) : (
                                <button
                                  onClick={() => assignAuditorRole(player.id, player.name)}
                                  className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                                  disabled={updatingRole !== null}
                                >
                                  <UserPlus className="w-4 h-4" />
                                  Make Auditor
                                </button>
                              )}
                            </>
                          )}
                        </div>

                        {/* Role Info */}
                        <div className="mt-3 text-xs text-gray-500">
                          {player.joinedAt && (
                            <p>Member since: {formatDate(player.joinedAt)}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-gray-600" />
            <p className="text-sm text-gray-700">
              <strong>Note:</strong> Members can have multiple roles. Admins who are also treasurers can both manage the club and approve expenses.
            </p>
          </div>
        </div>

        {showMigrateModal && (
          <MigratePlayersModal
            selectedPlayers={players.filter(p => selectedPlayerIds.has(p.id))}
            sourceClub={selectedClub}
            currentUser={currentUser}
            onClose={() => setShowMigrateModal(false)}
            onSuccess={() => {
              setShowMigrateModal(false);
              setSelectedPlayerIds(new Set());
              setMessage({ type: 'success', text: `Successfully migrated selected players!` });
              loadClubMembers();
            }}
          />
        )}
      </div>
    </div>
  );
};

export default PlayerRoleManagement;